/**
 * <nav-panel> Custom Element
 *
 * A drill-down alternative to <expandable-list> for the mobile nav. Instead of
 * expanding its child list downward, tapping the trigger swipes the current nav
 * content to the left and slides this panel's contents in from the right, full
 * width. The panel title is centered in the mobile nav header, with a back icon
 * on the left that returns to the list.
 *
 * Mechanics:
 *   - The mobile nav body is a two-layer track: a `.mobile-nav-base` layer (the
 *     list) and a `.mobile-nav-panels` overlay (this file teleports the active
 *     panel's contents into it). The slide itself is a CSS transition driven by
 *     the `panel-open` class on #mobile-nav (see _mobile-nav.scss) — the base
 *     translates to -100%, the overlay from 100% to 0, in lockstep.
 *   - Teleporting the contents into a sibling overlay (rather than animating an
 *     ancestor) avoids the transform-containing-block trap: a transform on the
 *     base layer would otherwise capture descendant panels.
 *   - One panel is active at a time; the shared back button (and a single
 *     `mobile-nav:close` reset listener) are wired once.
 *
 * Contract:
 *   - Adds 'nav-panel-init' after upgrade
 *   - Trigger click opens the panel; the header back button (or Escape) closes it
 *   - open()/close() are exposed for programmatic control
 *   - Resets (no animation) when the mobile nav closes (`mobile-nav:close`)
 *
 * Accessibility:
 *   - The off-screen layer is marked `inert` (base while open, overlay otherwise)
 *     so only the visible layer is focusable / in the a11y tree
 *   - Focus moves to the back button on open and returns to the trigger on close
 *   - Back button name is localized ("Back" / "Back to {title}") via data attrs
 *   - Collapsed contents stay `hidden`; the trigger is a disclosure
 *     (aria-expanded + aria-controls), not a menu
 *
 * AI: Entirely written by AI
 * Date: 2026-06-09
 * Model: Claude Opus 4.8
 * Updated: 2026-06-18 (Claude Opus 4.8) - Reset scroll position on open so
 *   horizontal scroll-snap strips (e.g. the search panel's New arrivals row)
 *   open at their start instead of pre-scrolled to the end.
 *
 * [NavPanel]
 */

import { log } from './log.js';

const INIT_CLASS = 'nav-panel-init';
const PANEL_OPEN_CLASS = 'panel-open';
const NAV_SELECTOR = '#mobile-nav';
const HOST_SELECTOR = '.js-mobile-nav-panels';
const BASE_SELECTOR = '.mobile-nav-base';
const BACK_SELECTOR = '.js-nav-panel-back';
const TITLE_SELECTOR = '.js-nav-panel-title';
const TRIGGER_SELECTOR = ':scope > .list-toggle-wrap > .js-nav-panel-trigger';
const CONTENTS_SELECTOR = ':scope > .js-nav-panel-contents';
// Panel contents opt a horizontal scroller into the open-at-start reset by
// tagging it with this class (see resetPanelScroll).
const SCROLL_RESET_SELECTOR = '.js-panel-scroll-reset';
const DURATION = 300;

// Single mobile nav per page, so a module-level active reference is sufficient.
let activePanel = null;
let backWired = false;
let navCloseWired = false;
let escWired = false;

function getNav() {
  return document.querySelector(NAV_SELECTOR);
}

// Localized back-button name: "Back to {title}" while open (from the
// data-back-template seeded by Liquid), plain "Back" otherwise.
function backLabel(back, title) {
  if (title && back.dataset.backTemplate) {
    return back.dataset.backTemplate.replace('__TITLE__', title);
  }
  return back.dataset.backLabel || 'Back';
}

// Header state for an open panel: back icon pinned left, title centered in the
// row. Both are hidden when no panel is open (the close button is always shown).
function setHeader(title, show) {
  const nav = getNav();
  if (!nav) return;
  const back = nav.querySelector(BACK_SELECTOR);
  const titleEl = nav.querySelector(TITLE_SELECTOR);
  if (back) {
    back.setAttribute('aria-label', backLabel(back, show ? title : ''));
    back.hidden = !show;
  }
  if (titleEl) {
    titleEl.textContent = show ? title : '';
    titleEl.hidden = !show;
  }
}

// Only the on-screen layer should be focusable / in the a11y tree: when a panel
// is open the base list is swiped off-screen, so mark it inert and un-inert the
// overlay (and vice versa). Without this, keyboard/SR users can reach the
// invisible base list behind the panel.
function setLayersInert(nav, panelOpen) {
  const base = nav.querySelector(BASE_SELECTOR);
  const host = nav.querySelector(HOST_SELECTOR);
  if (base) base.inert = panelOpen;
  if (host) host.inert = !panelOpen;
}

// A drill-down panel always opens at its start. The shared overlay host is the
// vertical scroller, so reset its scrollTop on every open. A panel that embeds
// a horizontal scroll-snap strip (the search panel's "New arrivals" row) tags
// it `.js-panel-scroll-reset` to be returned to its start too. Both matter
// because #mobile-nav is data-turbo-permanent: panel contents are reused across
// opens (moved back into <nav-panel> on close, not rebuilt), so a prior open's
// offset survives. The strip additionally re-snaps to its end when it's painted
// while the host is still parked off-screen at translateX(100%) during the
// slide-in — so reset again on the next frame to override the browser's
// post-layout snap pass.
//
// AI: Entirely written by AI
// Date: 2026-06-18
// Model: Claude Opus 4.8
function resetPanelScroll(host, contents) {
  const reset = () => {
    if (host) host.scrollTop = 0;
    if (!contents) return;
    contents.querySelectorAll(SCROLL_RESET_SELECTOR).forEach((el) => {
      el.scrollLeft = 0;
    });
  };
  reset();
  window.requestAnimationFrame(reset);
}

// Bound once: the back button always closes whatever panel is active.
function wireBackButtonOnce() {
  if (backWired) return;
  const nav = getNav();
  const back = nav && nav.querySelector(BACK_SELECTOR);
  if (!back) return;
  backWired = true;
  back.addEventListener('click', (event) => {
    event.preventDefault();
    if (activePanel) activePanel.close();
  });
}

// Bound once: when the mobile nav closes (header-nav dispatches this), drop any
// open panel instantly so the next open starts at the top level.
function wireNavCloseResetOnce() {
  if (navCloseWired) return;
  navCloseWired = true;
  document.addEventListener('mobile-nav:close', () => {
    const nav = getNav();
    if (nav) {
      nav.classList.remove(PANEL_OPEN_CLASS);
      setLayersInert(nav, false);
    }
    setHeader('', false);
    if (activePanel) {
      activePanel._reset();
      activePanel = null;
    }
  });
}

// Bound once: Escape steps back one level (closes the active panel) rather than
// dropping the whole drawer. Bubbling is stopped so it can't also trigger a
// drawer-level Escape handler added later.
function wireEscapeOnce() {
  if (escWired) return;
  escWired = true;
  document.addEventListener('keydown', (event) => {
    if ((event.key === 'Escape' || event.keyCode === 27) && activePanel) {
      event.stopPropagation();
      activePanel.close();
    }
  });
}

class NavPanel extends HTMLElement {
  connectedCallback() {
    if (this.classList.contains(INIT_CLASS)) return;
    this.classList.add(INIT_CLASS);

    this._trigger = this.querySelector(TRIGGER_SELECTOR);
    this._contents = this.querySelector(CONTENTS_SELECTOR);
    const titleEl = this._trigger && this._trigger.querySelector('.list-title');
    this._title = titleEl ? titleEl.textContent.trim() : '';
    this._closeTimer = null;

    if (this._trigger) {
      this._trigger.addEventListener('click', this._handleTrigger);
    }

    wireBackButtonOnce();
    wireNavCloseResetOnce();
    wireEscapeOnce();
  }

  _handleTrigger = (event) => {
    event.preventDefault();
    this.open();
  };

  open() {
    const nav = getNav();
    const host = nav && nav.querySelector(HOST_SELECTOR);
    if (!nav || !host || !this._contents) return;

    window.clearTimeout(this._closeTimer);

    // Switching directly from another panel — detach it first.
    if (activePanel && activePanel !== this) activePanel._reset();
    activePanel = this;

    this._contents.hidden = false;
    host.appendChild(this._contents);
    // Always open at the start — clear any leftover / re-snapped scroll offset.
    resetPanelScroll(host, this._contents);

    setHeader(this._title, true);
    if (this._trigger) this._trigger.setAttribute('aria-expanded', 'true');
    nav.classList.add(PANEL_OPEN_CLASS);
    // Inert the off-screen base before moving focus into the panel.
    setLayersInert(nav, true);

    const back = nav.querySelector(BACK_SELECTOR);
    if (back) back.focus();

    log('[NavPanel] open: ' + this._title);
  }

  close({ animate = true, restoreFocus = true } = {}) {
    const nav = getNav();
    if (!nav) return;

    nav.classList.remove(PANEL_OPEN_CLASS);
    setLayersInert(nav, false);
    setHeader('', false);
    if (this._trigger) this._trigger.setAttribute('aria-expanded', 'false');

    window.clearTimeout(this._closeTimer);
    if (animate) {
      // Let the slide-out finish before moving the contents back into the list.
      this._closeTimer = window.setTimeout(() => this._restoreContents(), DURATION);
    } else {
      this._restoreContents();
    }

    if (activePanel === this) activePanel = null;
    if (restoreFocus && this._trigger) this._trigger.focus();

    log('[NavPanel] close: ' + this._title);
  }

  // Instant detach (no animation) — used when switching panels or on nav close.
  _reset() {
    window.clearTimeout(this._closeTimer);
    this._restoreContents();
    if (this._trigger) this._trigger.setAttribute('aria-expanded', 'false');
  }

  _restoreContents() {
    if (!this._contents) return;
    this._contents.hidden = true;
    if (this._contents.parentElement !== this) {
      this.appendChild(this._contents);
    }
  }
}

customElements.define('nav-panel', NavPanel);
log('[NavPanel] registered');

export { NavPanel };
